import React, { useState, useRef } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { sql } from "@codemirror/lang-sql";
import { styled } from "@mui/material/styles";
import { Box, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import Button from "../Button/Button";
import Modal from "../Modal/Modal";
import "./SqlEditor.css";

import { EditorView, Decoration } from "@codemirror/view";
import { RangeSetBuilder, StateField, StateEffect } from "@codemirror/state";

const EditorContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isExpanded",
})(({ isExpanded }) => ({
  position: "fixed",
  top: 0,
  right: isExpanded ? 0 : -450,
  width: 450,
  height: "100vh",
  backgroundColor: "#ffffff",
  borderLeft: "1px solid #e1e5e9",
  borderRadius: "2px 0 0 2px",
  transition: "right 0.3s ease-in-out",
  // When expanded, ensure SQL editor stays above MUI popovers/menus (default ~1300)
  zIndex: isExpanded ? 1400 : 1000,
  display: "flex",
  flexDirection: "column",
  boxShadow: "-2px 0 10px rgba(0, 0, 0, 0.1)",
}));

const EditorHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "16px 20px",
  background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
  borderBottom: "1px solid #e1e5e9",
  color: "#1e293b",
  fontWeight: 600,
});

const EditorContent = styled(Box)({
  flex: 1,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  backgroundColor: "#ffffff",
});

const CodeMirrorWrapper = styled(Box)({
  flex: 1,
  minHeight: 0,
  height: "100%",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  "& > div": {
    height: "100%",
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  "& .cm-editor": {
    height: "100%",
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    fontSize: "14px",
    fontFamily:
      "'JetBrains Mono', 'Fira Code', 'Monaco', 'Menlo', 'Ubuntu Mono', monospace",
  },
  "& .cm-editor .cm-scroller": {
    lineHeight: "1.6",
    flex: "1 1 0",
    minHeight: 0,
    overflowX: "auto",
    overflowY: "scroll",
  },
  "& .cm-editor .cm-content": { padding: "16px" },
});

const EditorFooter = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "16px 20px",
  background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
  borderTop: "1px solid #e1e5e9",
  color: "#64748b",
});

const ExecuteButton = styled(Button)({
  background: "#3F49A7 !important",
  color: "#fff !important",
  borderRadius: "6px",
  padding: "8px 16px",
  fontWeight: 600,
  fontSize: "13px",
  height: "32px",
  minWidth: "auto",
  transition: "all 0.2s ease-in-out",
  boxShadow: "0 2px 4px rgba(63, 73, 167, 0.13)",
  display: "flex",
  alignItems: "center",
  gap: 8,
  "&:hover": {
    background: "#3F49A7 !important",
    color: "#fff !important",
    boxShadow: "0 4px 8px rgba(63, 73, 167, 0.18)",
    transform: "translateY(-1px)",
  },
  "&:disabled": {
    background: "#9ca3af !important",
    color: "#fff !important",
    transform: "none",
    boxShadow: "none",
  },
});

const setErrorLinesEffect = StateEffect.define();

const errorHighlightField = StateField.define({
  create() {
    return Decoration.none;
  },
  update(deco, tr) {
    deco = deco.map(tr.changes);
    for (const e of tr.effects) {
      if (e.is(setErrorLinesEffect)) {
        const lines = Array.isArray(e.value) ? e.value : null;
        if (!lines || lines.length === 0) return Decoration.none;

        // unique + in-range + sorted
        const doc = tr.state.doc;
        const uniq = Array.from(new Set(lines))
          .filter((n) => n >= 1 && n <= doc.lines)
          .sort((a, b) => a - b);

        const b = new RangeSetBuilder();
        for (const n of uniq) {
          const ln = doc.line(n);
          b.add(ln.from, ln.from, Decoration.line({ class: "cm-errorLine" }));
        }
        return b.finish();
      }
    }
    return deco;
  },
  provide: (f) => EditorView.decorations.from(f),
});

const SqlEditor = ({
  sqlQuery,
  onSqlChange,
  onExecuteQuery, // Promise<{ data: { runQuery: { success, message, data? }}}>
  isExecuting = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMsg, setModalMsg] = useState("");
  const editorRef = useRef(null); // { view }

  // State to store user_db_id and db_type from pasted JSON
  const [pastedUserDbId, setPastedUserDbId] = useState(null);
  const [pastedDbType, setPastedDbType] = useState(null);

  const handleToggle = () => {

    if (!isExpanded && sqlQuery) {
      const formatted = formatSqlQuery(sqlQuery);
      if (formatted !== sqlQuery) {
        onSqlChange?.(formatted);
      }
    }
    setIsExpanded((prev) => !prev);
  };

  // Handle paste event to detect JSON with query, user_db_id, and db_type
  const handlePaste = (event) => {
    const pastedText = event.clipboardData?.getData("text");
    if (!pastedText) return;

    try {
      const parsed = JSON.parse(pastedText);
      // Check if it's a valid query object with user_db_id and db_type
      if (parsed && typeof parsed === "object" && parsed.query) {
        event.preventDefault();

        // Store user_db_id and db_type for execute
        if (parsed.user_db_id !== undefined) {
          setPastedUserDbId(parsed.user_db_id);
        }
        if (parsed.db_type !== undefined) {
          setPastedDbType(parsed.db_type);
        }

        // Clean up the query - remove any extra quotes
        let cleanQuery = parsed.query || "";
        cleanQuery = cleanQuery.replace(/"""/g, '"');
        cleanQuery = cleanQuery.replace(/""/g, '"');

        // Only paste the query text into the editor
        onSqlChange?.(cleanQuery);
      }
    } catch (e) {
      // Not JSON, let the default paste behavior handle it
    }
  };

  const formatSqlQuery = (query) => {
    if (!query) return "";
    let cleaned = query.replace(/"{3,}/g, '"').replace(/"{2,}/g, '"');
    let formatted = cleaned
      .replace(
        /\b(SELECT|FROM|WHERE|GROUP BY|ORDER BY|HAVING|JOIN|LEFT JOIN|RIGHT JOIN|INNER JOIN|OUTER JOIN|UNION|INSERT|UPDATE|DELETE|CREATE|ALTER|DROP)\b/gi,
        "\n$1"
      )
      .replace(
        /\b(AND|OR|IN|NOT IN|LIKE|NOT LIKE|IS NULL|IS NOT NULL|BETWEEN|EXISTS|NOT EXISTS)\b/gi,
        "\n$1"
      )
      .replace(/,/g, ",\n")
      .replace(/\(/g, "(\n")
      .replace(/\)/g, "\n)")
      .replace(/\n\s*\n/g, "\n")
      .split("\n")
      .map((line) => {
        const t = line.trim();
        if (!t) return "";
        let indent = 2;
        if (t.startsWith(")")) indent = 0;
        else if (
          t.match(
            /^(FROM|WHERE|GROUP BY|ORDER BY|HAVING|JOIN|LEFT JOIN|RIGHT JOIN|INNER JOIN|OUTER JOIN|UNION)/i
          )
        )
          indent = 0;
        else if (
          t.match(
            /^(AND|OR|IN|NOT IN|LIKE|NOT LIKE|IS NULL|IS NOT NULL|BETWEEN|EXISTS|NOT EXISTS)/i
          )
        )
          indent = 2;
        else if (t.startsWith(",")) indent = 2;
        else if (t.startsWith("(")) indent = 0;
        return "  ".repeat(indent) + t;
      })
      .filter(Boolean)
      .join("\n");
    return formatted;
  };



  const onCreateEditor = (view) => {
    editorRef.current = { view };
  };

  // extract ALL "line N" occurrences
  const parseAllErrorLines = (message) => {
    const re = /line\s+(\d+)/gi;
    const lines = [];
    let m;
    while ((m = re.exec(message))) {
      const n = parseInt(m[1], 10);
      if (!Number.isNaN(n)) lines.push(n);
    }
    return lines;
  };

 const isReadOnlyQuery = (query) => {
    if (!query) return false;

    // Remove comments 
    let cleaned = query
      .replace(/--.*$/gm, "")
      .replace(/\/\*[\s\S]*?\*\//g, "")
      .trim()
      .toLowerCase();

    // Remove leading parentheses
    while (cleaned.startsWith("(")) {
      cleaned = cleaned.slice(1).trim();
    }

    // ❌ Block multi-statements
    if (cleaned.includes(";")) return false;

    // ✅ Allow only read operations
    const allowedStart = /^(select|with|show|describe)\b/;

    return allowedStart.test(cleaned);
  };

  const handleExecute = async () => {
    if (!onExecuteQuery || !sqlQuery) return;

    if (!isReadOnlyQuery(sqlQuery)) {
      setModalMsg("Only SELECT queries are allowed. DDL/DML operations are not permitted.");
      setModalOpen(true);

      if (editorRef.current) {
        editorRef.current.view.dispatch({
          effects: setErrorLinesEffect.of(null),
        });
      }
      return;
    }

    try {
      // Format the SQL query before executing
      const formatted = formatSqlQuery(sqlQuery);
      // Update the editor content to the formatted query
      if (editorRef.current) {
        const view = editorRef.current.view;
        view.dispatch({
          changes: { from: 0, to: view.state.doc.length, insert: formatted },
        });
      }
      // Pass user_db_id and db_type if they were extracted from pasted JSON
      const res = await onExecuteQuery(sqlQuery, pastedUserDbId, pastedDbType);
      const runQuery = res?.data?.runQuery;

      if (!runQuery?.success) {
        const msg = runQuery?.message || "Query failed with no error message.";
        const lines = parseAllErrorLines(msg); // ← multiple lines supported

        setModalMsg(msg);
        setModalOpen(true);

        if (editorRef.current) {
          const view = editorRef.current.view;
          view.dispatch({
            effects: setErrorLinesEffect.of(lines.length ? lines : null),
          });
          if (lines.length) {
            const first = view.state.doc.line(Math.max(1, lines[0]));
            view.dispatch({
              effects: EditorView.scrollIntoView(first.from, { y: "center" }),
            });
          }
        }
        return;
      }

      // success → clear highlight & close modal
      if (editorRef.current) {
        editorRef.current.view.dispatch({
          effects: setErrorLinesEffect.of(null),
        });
      }
      setModalOpen(false);
    } catch (err) {
      const msg = err?.message || "Unexpected error while running query.";
      setModalMsg(msg);
      setModalOpen(true);
      if (editorRef.current) {
        editorRef.current.view.dispatch({
          effects: setErrorLinesEffect.of(null),
        });
      }
    }
  };

  return (
    <>
      <IconButton
        onClick={handleToggle}
        aria-label="Toggle SQL Editor"
        sx={{
          position: "fixed",
          top: "10%",
          right: isExpanded ? 450 : 0,
          transform: "translateY(-50%)",
          background: "linear-gradient(135deg, #3F49A7 0%, #6366f1 100%)",
          color: "#fff",
          borderRadius: "2px 0 0 2px",
          padding: "8px 6px",
          zIndex: isExpanded ? 1399 : 999,
          boxShadow: "0 2px 8px rgba(99, 102, 241, 0.3)",
          "&:hover": {
            background: "linear-gradient(135deg, #2e357a 0%, #4f46e5 100%)",
          },
        }}
      >
        <MenuOpenIcon />
      </IconButton>

      <EditorContainer isExpanded={isExpanded}>
        <EditorHeader>
          <Typography
            variant="h6"
            sx={{ color: "#1e293b", fontSize: "16px", fontWeight: 600 }}
          >
            SQL Query Editor
          </Typography>
          <IconButton
            onClick={handleToggle}
            sx={{ color: "#64748b" }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </EditorHeader>

        <EditorContent>
          <CodeMirrorWrapper onPaste={handlePaste}>
            <CodeMirror
              value={sqlQuery}
              height="100%"
              minHeight="0"
              extensions={[sql(), errorHighlightField]}
              onChange={(value) => {
                onSqlChange?.(value);
                // Clear pasted metadata when user manually edits
                setPastedUserDbId(null);
                setPastedDbType(null);
                if (editorRef.current) {
                  editorRef.current.view.dispatch({
                    effects: setErrorLinesEffect.of(null),
                  });
                }
              }}
              theme="light"
              basicSetup={{
                lineNumbers: true,
                highlightActiveLineGutter: true,
                foldGutter: true,
              }}
              onCreateEditor={onCreateEditor}
            />
          </CodeMirrorWrapper>
        </EditorContent>

        <EditorFooter>
          <Typography
            variant="body2"
            sx={{ color: "#64748b", fontSize: "13px" }}
          >
            {sqlQuery
              ? `${sqlQuery.split("\n").length} lines`
              : "No query"}
          </Typography>
          <ExecuteButton
            onClick={handleExecute}
            disabled={!sqlQuery || isExecuting}
            size="small"
            variant="contained"
          >
            <PlayArrowIcon sx={{ fontSize: "16px" }} />
            Execute
          </ExecuteButton>
        </EditorFooter>
      </EditorContainer>

      {modalOpen && (
        <Modal
          type="error"
          title="Query Failed"
          message={modalMsg}
          onClose={() => setModalOpen(false)}
        />
      )}
    </>
  );
};

export default React.memo(SqlEditor);
